import { type ReactNode, useId, useState } from 'react';

import { motion } from 'motion/react';

import type { TipJarCopy } from './TipJar.types';
import * as styles from './TipJarAmountPicker.css';
import { parseWholeAmount } from './tipJar.utils';

const CUSTOM_AMOUNT = 'custom';

type AmountSelection = number | typeof CUSTOM_AMOUNT;

type TipJarAmountPickerProps = {
  presetAmountsCents: number[];
  minimumAmountCents: number;
  maximumAmountCents: number;
  initialAmountCents?: number;
  currencySymbol: string;
  formatAmount: (amountCents: number) => string;
  copy: Pick<
    TipJarCopy,
    'chipIn' | 'enterAmount' | 'amountLabel' | 'otherAmount' | 'customAmountLabel' | 'amountOutOfRange'
  >;
  isBusy?: boolean;
  note?: ReactNode;
  onChipIn: (amountCents: number) => void;
  chargeCents?: (amountCents: number) => number;
  children?: (amountCents: number | null) => ReactNode;
};

const initialSelection = (presetAmountsCents: number[], initialAmountCents?: number): AmountSelection => {
  if (initialAmountCents === undefined) return presetAmountsCents[0];

  return presetAmountsCents.includes(initialAmountCents) ? initialAmountCents : CUSTOM_AMOUNT;
};

export const TipJarAmountPicker = ({
  presetAmountsCents,
  minimumAmountCents,
  maximumAmountCents,
  initialAmountCents,
  currencySymbol,
  formatAmount,
  copy,
  isBusy = false,
  note,
  onChipIn,
  chargeCents,
  children,
}: TipJarAmountPickerProps) => {
  const optionsName = useId();
  const customInputId = useId();
  const hintId = useId();
  const [pickedSelection, setSelection] = useState(() => initialSelection(presetAmountsCents, initialAmountCents));
  // The writer's preview swaps the presets under a mounted picker, so a pick that is no longer offered starts over.
  const selection =
    pickedSelection === CUSTOM_AMOUNT || presetAmountsCents.includes(pickedSelection)
      ? pickedSelection
      : initialSelection(presetAmountsCents, initialAmountCents);
  // Only a reader who picks "Other" gets the keyboard; a custom amount from their link does not.
  const [hasPickedCustom, setHasPickedCustom] = useState(false);
  const [customInput, setCustomInput] = useState(() =>
    selection === CUSTOM_AMOUNT && initialAmountCents ? String(initialAmountCents / 100) : '',
  );

  const isCustom = selection === CUSTOM_AMOUNT;
  const amountCents = isCustom ? parseWholeAmount(customInput) : selection;
  const isAmountValid = amountCents !== null && amountCents >= minimumAmountCents && amountCents <= maximumAmountCents;
  const showRangeHint = isCustom && customInput !== '' && !isAmountValid;
  const chargedCents = amountCents === null ? null : (chargeCents?.(amountCents) ?? amountCents);
  const chipInLabel = chargedCents === null ? copy.enterAmount : copy.chipIn(formatAmount(chargedCents));

  const select = (option: AmountSelection) => {
    setSelection(option);
    setHasPickedCustom(option === CUSTOM_AMOUNT);
  };

  return (
    <form
      className={styles.picker}
      onSubmit={(event) => {
        event.preventDefault();
        if (isAmountValid) onChipIn(amountCents);
      }}
    >
      <fieldset className={styles.amounts}>
        <legend className={styles.legend}>{copy.amountLabel}</legend>

        <div className={styles.options} data-count={presetAmountsCents.length + 1}>
          {presetAmountsCents.map((option) => (
            <label key={option} className={styles.option} data-checked={selection === option}>
              <input
                type="radio"
                name={optionsName}
                className={styles.radio}
                checked={selection === option}
                onChange={() => select(option)}
              />
              {formatAmount(option)}
            </label>
          ))}

          <div className={styles.otherOption}>
            <label className={styles.option} data-checked={isCustom}>
              <input
                type="radio"
                name={optionsName}
                className={styles.radio}
                checked={isCustom}
                onChange={() => select(CUSTOM_AMOUNT)}
              />
              {copy.otherAmount}
            </label>

            {isCustom && (
              <motion.label
                htmlFor={customInputId}
                className={styles.customAmount}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
              >
                <span className={styles.currencySymbol} aria-hidden>
                  {currencySymbol}
                </span>
                <input
                  id={customInputId}
                  className={styles.customAmountInput}
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label={copy.customAmountLabel}
                  aria-invalid={showRangeHint}
                  aria-describedby={showRangeHint ? hintId : undefined}
                  value={customInput}
                  // biome-ignore lint/a11y/noAutofocus: the reader just picked "Other" to type an amount
                  autoFocus={hasPickedCustom}
                  onChange={(event) => setCustomInput(event.target.value)}
                />
              </motion.label>
            )}
          </div>
        </div>

        {showRangeHint && (
          <p id={hintId} className={styles.hint} role="alert">
            {copy.amountOutOfRange(formatAmount(minimumAmountCents), formatAmount(maximumAmountCents))}
          </p>
        )}
      </fieldset>

      {children?.(isAmountValid ? amountCents : null)}

      {note}

      <motion.button
        type="button"
        className={styles.chipIn}
        onClick={() => {
          if (amountCents !== null && isAmountValid) onChipIn(amountCents);
        }}
        disabled={!isAmountValid || isBusy}
        aria-busy={isBusy}
        whileTap={{ scale: 0.98 }}
      >
        {chipInLabel}
      </motion.button>
    </form>
  );
};
